import React, { useState, useContext, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
  Stack,
  Menu,
  MenuItem,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import authContext from '../../AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(authContext);
  const { isLoggedIn, userType } = auth;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleLogout();
    handleMenuClose();
  };

  useEffect(() => {
    if (
      userType === 'mng' &&
      (location.pathname === '/' || location.pathname === '/student-details')
    ) {
      navigate('/add-student');
    }
  }, [userType, location.pathname, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    auth.signout();
    navigate(userType === 'mng' ? '/student-details' : '/login');
  };

  const menuItems = [
    ...(userType !== 'mng' ? [{ text: 'Dashboard', path: '/' }] : []),
    ...(userType !== 'ad' && userType !== 'mng'
      ? [{ text: 'Branches', path: '/branches' }]
      : []),
    ...(userType !== 'mng' ? [{ text: 'Batches', path: '/batches' }] : []),
    ...(userType !== 'mng' && userType !== 'ad'
      ? [{ text: 'Managers', path: '/managers' }]
      : []),
    { text: 'Student Details', path: '/student-details' },
    { text: 'Change Password', path: '/change-password' },
  ];

  const renderLinks = () =>
    menuItems.map((item, index) => (
      <ListItem
        button
        key={index}
        component={Link}
        to={item.path}
        onClick={() => setIsMenuOpen(false)}
        sx={{ color: 'white' }}
      >
        <ListItemText
          primary={item.text}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 18,
            textDecoration:
              location.pathname === item.path ? 'underline' : 'none',
            fontWeight: location.pathname === item.path ? 'bold' : 'normal',
          }}
        />
      </ListItem>
    ));

  return (
    <AppBar position="static" sx={{ bgcolor: '#15803d', boxShadow: 3 }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <VisibilityIcon sx={{ mr: 1 }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', letterSpacing: 1 }}
          >
            IRIS
          </Typography>
        </Box>

        {isLoggedIn && (
          <>
            {/* Desktop Links */}
            {!isMobile ? (
              <Stack direction="row" alignItems="center" spacing={2}>
                {menuItems.map((item, index) => (
                  <Button
                    key={index}
                    color="inherit"
                    component={Link}
                    to={item.path}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: 18,
                      textDecoration:
                        location.pathname === item.path ? 'underline' : 'none',
                    }}
                  >
                    {item.text}
                  </Button>
                ))}

                {/* Username with dropdown menu */}
                <Box>
                  <Button
                    onClick={handleMenuClick}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <AccountCircleIcon />
                    Hi, {auth.username}
                  </Button>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem onClick={handleLogoutClick}>
                      <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </Box>
              </Stack>
            ) : (
              <>
                {/* Mobile Hamburger Menu */}
                <IconButton color="inherit" onClick={toggleMenu}>
                  <MenuIcon />
                </IconButton>
                <Drawer anchor="right" open={isMenuOpen} onClose={toggleMenu}>
                  <Box
                    sx={{
                      width: 250,
                      p: 2,
                      bgcolor: '#15803d',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <AccountCircleIcon sx={{ color: 'white' }} />
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: 'white' }}
                        >
                          Hi, {auth.username}
                        </Typography>
                      </Stack>

                      <IconButton onClick={toggleMenu} sx={{ color: 'white' }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    <List>
                      {renderLinks()}
                      <Divider sx={{ my: 1 }} />
                      <ListItem button onClick={handleLogout}>
                        <LogoutIcon sx={{ color: 'red', mr: 1 }} />
                        <ListItemText
                          primary="Logout"
                          primaryTypographyProps={{
                            sx: { color: 'red', fontWeight: 'bold' },
                          }}
                        />
                      </ListItem>
                    </List>
                  </Box>
                </Drawer>
              </>
            )}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
